import * as React from 'react';
import Autocomplete from 'react-autocomplete';
import matchSorter from 'match-sorter';
import isEmail from './isEmail';
import "./reactMultiTextarea.css";

export interface IReactMultiTextareaProps {
  elements: string[];
  isEmailElement: boolean;
  onChange?: (emails: string[]) => void;
  dropdownKeys: { key: string, value: string };
  style?: object;
  getLabel: (
    email: string,
    index: number,
    removeElement: (index: number) => void,
  ) => void;
  className?: string;
  placeholder?: string | React.ReactNode;
  autoCompleteValues: any[];
}

export interface IReactMultiTextareaState {
  focused?: boolean;
  elements: string[];
  inputValue?: string;
  autoCompleteValues?: any[];
}

class ReactMultiTextarea extends React.Component<IReactMultiTextareaProps, IReactMultiTextareaState> {
  constructor(props: IReactMultiTextareaProps) {
    super(props);
    this.state = {
      focused: false,
      elements: [],
      inputValue: '',
      autoCompleteValues: []
    }
  }
  private input: HTMLInputElement;

  public static getDerivedStateFromProps(
    nextProps: IReactMultiTextareaProps,
    prevState: IReactMultiTextareaState,
  ) {
    if (prevState.elements !== nextProps.elements) {
      return {
        elements: nextProps.elements || [],
        focused: false
      };
    }
    return null;
  }

  private addElement = (elementValue: any, validElements: any[]) => {
    const elements: any[] = this.state.elements;
    return elements.find((index) => elements[index] === elementValue) ? false : validElements.push(elementValue);
  };

  public onChangeInputValue = (value: string, isEnter?: boolean) => {
    const validElements: string[] = [];
    let inputValue: string = '';
    const re = /[ ,;]/g;

    if (this.props.isEmailElement && re.test(value)) {
      const arr = value.split(re).filter(n => {
        return n !== '' && n !== undefined && n !== null;
      });
      do {
        if (isEmail('' + arr[0])) {
          this.addElement('' + arr.shift(), validElements);
        } else {
          if (arr.length === 1) {
            inputValue = '' + arr.shift();
          } else {
            arr.shift();
          }
        }
      } while (arr.length);
    } else if (isEnter) {
      if (this.props.isEmailElement && isEmail(value)) {
        this.addElement(value, validElements);
      } else if (value !== '') {
        const dropdownElement = this.getDropdownElement(value);
        if (dropdownElement && dropdownElement[this.props.dropdownKeys.key] && dropdownElement[this.props.dropdownKeys.key] !== '') {
          this.addElement(dropdownElement[this.props.dropdownKeys.key], validElements)
        } else {
          inputValue = value;
        }
      } else {
        inputValue = value;
      }
    } else if (!this.props.isEmailElement && value !== '') {
      const dropdownElement = this.getDropdownElement(value);
      if (dropdownElement && dropdownElement[this.props.dropdownKeys.key] && dropdownElement[this.props.dropdownKeys.key] !== '') {
        this.addElement(dropdownElement[this.props.dropdownKeys.key], validElements)
      } else {
        inputValue = value;
      }
    } else {
      inputValue = value;
    }
    this.setState({
      elements: [...this.state.elements, ...validElements],
      inputValue,
      focused: false
    });

    if (validElements.length && this.props.onChange) {
      this.props.onChange([...this.state.elements, ...validElements]);
    }
  };

  public getDropdownElements = () => this.props.autoCompleteValues.filter(provider => !this.state.elements.find(stateElement => stateElement === provider[this.props.dropdownKeys.key]));

  public getDropdownElement = (value) => this.props.autoCompleteValues.find(dropdownValue => dropdownValue[this.props.dropdownKeys.value] === value);

  public removeElement = (index: number) => {
    if (this.props.onChange) {
      const dropdownData = this.getDropdownElements();
      this.setState({ focused: false, inputValue: '', autoCompleteValues: dropdownData });
      this.props.onChange([
        ...this.state.elements.slice(0, index),
        ...this.state.elements.slice(index + 1),
      ]);
    }
  };

  public onSearchChange = (search) => {
    if (search) {
      const dropdownData = this.getDropdownElements();
      const filteredData = matchSorter(dropdownData, search, { keys: [this.props.dropdownKeys.value] });
      this.setState({ inputValue: search, autoCompleteValues: [...filteredData] });
    }
  }

  public render() {
    const { focused, elements, inputValue, autoCompleteValues } = this.state;
    const { style, getLabel, className = '', placeholder, isEmailElement } = this.props;

    return (
      <div
        className={`${className} react-multi-email ${
          focused ? 'focused' : ''
          } ${inputValue === '' && elements.length === 0 ? 'empty' : ''}`}
        style={style}
        onClick={() => {
          this.input.focus();
        }}
      >
        {placeholder ? <span data-placeholder={true}>{placeholder}</span> : null}
        {elements.map((element: string, index: number) =>
          getLabel(element, index, this.removeElement),
        )}
        <input type="text"
          className={isEmailElement ? '' : 'multi-text-area-input'}
          ref={ref => {
            if (ref && isEmailElement) {
              this.input = ref;
            }
          }}
          value={inputValue}
          onChange={(e) => {
            if (isEmailElement) {
              this.onChangeInputValue(e.target.value);
            }
          }}
          onBlur={(e: any) => {
            this.onChangeInputValue(e.target.value, true);
            this.setState({ focused: false });
          }}
          onKeyDown={(e: any) => {
            if (e.which === 8 && !e.target.value) {
              this.removeElement(this.state.elements.length - 1);
              this.setState({ focused: false });
            }
          }}
          onKeyUp={(e: any) => {
            if (e.which === 13) {
              this.onChangeInputValue(e.target.value, true);
              this.setState({ focused: false });
            }
          }}
        />
        {!isEmailElement && <Autocomplete
          ref={ref => {
            if (ref && !isEmailElement) {
              this.input = ref;
            }
          }}
          getItemValue={(item) => item.name}
          items={autoCompleteValues}
          value={inputValue}
          renderItem={(item, isHighlighted) =>
            <div key={item.name} style={{ background: isHighlighted ? 'lightgray' : 'white' }}>
              {item.name}
            </div>}
          menuStyle={{
            left: 'auto',
            top: 'auto',
            borderRadius: '3px',
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.1)',
            background: 'rgba(255, 255, 255, 0.9)',
            fontSize: '90%',
            position: 'absolute',
            overflow: 'auto',
            height: 'auto',
            maxHeight: '100px',
            zIndex: '1'
          }}
          inputProps={{ className: "input-transparent" }}
          onFocus={() => this.setState({ focused: true })}
          onBlur={(e: any) => {
            this.onChangeInputValue(e.target.value, true);
            this.setState({ focused: false });
          }}
          onChange={(e) => {
            this.onChangeInputValue(e.target.value);
            this.onSearchChange(e.target.value)
          }}
          onSelect={(val) => {
            this.onChangeInputValue(val, true);
            this.setState({ focused: false });
          }}
          onKeyDown={(e: any) => {
            if (e.which === 8 && !e.target.value) {
              this.removeElement(this.state.elements.length - 1);
            }
          }}
          onKeyUp={(e: any) => {
            if (e.which === 13) {
              this.onChangeInputValue(e.target.value, true);
              this.setState({ focused: false });
            }
          }}
        />}
      </div>
    );
  }
}

export default ReactMultiTextarea;
